import { useEffect, useState } from 'react';
import i18n from '../i18n';

/**
 * Availability, answered as a MOMENT rather than a state.
 *
 * "In stock" / "within 24 h" described our side of the transaction. The customer at the counter is
 * asking one question — when can I have it — so the column answers in their terms and never in
 * ours: `Today`, `Tomorrow 13:00`, `Lundi 13:00`. No "J+1", no "within 24 h"; those are internal
 * shorthand that puts the arithmetic back on the person being quoted.
 *
 * THE RULE, and it is the server's — `DeliveryPromiseService` is the authority, this is the same
 * algorithm run locally so the screen can quote without a round-trip per keystroke:
 *
 *   1. before `supplierCutoffTime` (14:20) the order joins today's collection, otherwise tomorrow's;
 *   2. delivery is the Nth working day STRICTLY AFTER that collection day, at `supplierDeliveryTime`
 *      (13:00) — N is 1 for Inter Cars' standard round;
 *   3. weekends and `business_holidays` (migration 088) are not working days.
 *
 * Framing it around the collection day is what makes the Friday case fall out with no special
 * casing: Friday 10:00 collects Friday, and the next working day is Monday because Saturday is not
 * one. Friday 14:21 collects "Saturday", and the next working day after Saturday is still Monday.
 * The weekend absorbs the slip.
 *
 * Both hours and the closure list arrive from `/meta/sales-config`, so this side and the server
 * cannot disagree about a promise they will both make.
 */
export const PARIS_TZ = 'Europe/Paris';

/**
 * Weekday names for the promise line. Deliberately NOT `Intl.DateTimeFormat`: that yields
 * lowercase ("lundi"), and this reads as a label. Indexed by `Date.getUTCDay()`, so Sunday leads.
 * Read off the catalogue at call time, so the promise speaks the operator's language.
 */
export const weekdays = (): string[] =>
  i18n.t('delivery.weekdays', { returnObjects: true }) as string[];

/** Today's civil date and minute-of-day in Paris — the frame the cut-off is written against. */
export function parisNow(nowMs: number): { y: number; m: number; d: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PARIS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(nowMs));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return { y: n('year'), m: n('month'), d: n('day'), minute: n('hour') * 60 + n('minute') };
}

export interface Eta {
  /** The whole line, as the column shows it: "Today", "Tomorrow 13:00", "Lundi 13:00". */
  label: string;
  /** Everything the one-line answer had to leave out, for the cell's tooltip. */
  detail: string;
  /** Same day as the sale: worth painting green. */
  immediate: boolean;
  /** The promised day as an instant, so a shift can be DETECTED and not just re-rendered. */
  at: number;
}

export const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

export interface DeliveryCalendar {
  cutoff: string;
  deliveryTime: string;
  /** "YYYY-MM-DD" closures — public holidays and our own, straight from business_holidays. */
  holidays: string[];
}

/** Mirrors DeliveryPromiseService.promisedDay — keep the two in step. */
export function promisedDay(leadWorkingDays: number, cal: DeliveryCalendar, nowMs: number): Date {
  const { y, m, d, minute } = parisNow(nowMs);
  // UTC arithmetic on a civil date: no DST hour can shift which day we land on.
  const cursor = new Date(Date.UTC(y, m - 1, d));
  if (leadWorkingDays <= 0) return cursor;

  const closed = new Set(cal.holidays);
  const isWorking = (dt: Date): boolean => {
    const dow = dt.getUTCDay();
    return dow !== 0 && dow !== 6 && !closed.has(isoDay(dt));
  };
  // Past the cut-off the order joins TOMORROW's collection. The collection day is deliberately not
  // forced onto a working day — the walk below does that, which is how Friday-after-cut-off lands
  // on Monday rather than Tuesday.
  if (minute >= toMinutes(cal.cutoff)) cursor.setUTCDate(cursor.getUTCDate() + 1);
  for (let walked = 0; walked < leadWorkingDays; ) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    if (isWorking(cursor)) walked++;
  }
  return cursor;
}

export function deliveryEta(leadWorkingDays: number, cal: DeliveryCalendar, nowMs: number): Eta {
  const { y, m, d, minute } = parisNow(nowMs);
  const start = Date.UTC(y, m - 1, d);
  const date = promisedDay(leadWorkingDays, cal, nowMs);
  const calendarDays = Math.round((date.getTime() - start) / 86_400_000);
  const missedToday = minute >= toMinutes(cal.cutoff) && leadWorkingDays > 0;

  /* Beyond tomorrow a relative phrase stops being readable at a glance ("day after tomorrow" is
     already a mouthful) — the weekday is what the counter actually says. Leads are short enough
     that a bare weekday can't wrap around to an ambiguous week; the tooltip carries the date. */
  const day =
    calendarDays === 0
      ? i18n.t('delivery.today')
      : calendarDays === 1
        ? i18n.t('delivery.tomorrow')
        : weekdays()[date.getUTCDay()];
  // Same day = it is on our shelf right now, so there is no arrival hour to quote.
  const label = calendarDays === 0 ? day : `${day} ${cal.deliveryTime}`;

  const detail = [
    new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date),
    leadWorkingDays <= 0
      ? i18n.t('delivery.inStockNow')
      : i18n.t('delivery.supplierRound', { count: leadWorkingDays }),
    missedToday
      ? i18n.t('delivery.cutoffPassed', { cutoff: cal.cutoff })
      : leadWorkingDays > 0
        ? i18n.t('delivery.orderBy', { cutoff: cal.cutoff })
        : '',
    i18n.t('delivery.excluded'),
  ]
    .filter(Boolean)
    .join('\n');

  return { label, detail, immediate: calendarDays === 0, at: date.getTime() };
}

/**
 * A SUPPLIER's own date, as the backend relays it (backend/src/services/purchasing/deliverySlots.ts):
 * this many units arrive at `at`, if ordered before `orderBy`. Replaces the fixed 24 h / 48 h
 * round above for every supplier line — the real date depends on which of the supplier's
 * warehouses holds the part and on that warehouse's cut-off, and only the supplier knows both.
 */
export interface DeliverySlot {
  qty: number;
  at: string;
  /** False when the supplier gave a day only (Inter Cars): show the day, not an hour. */
  hourKnown: boolean;
  orderBy: string | null;
  warehouse?: string | null;
}

/** Mirrors the backend's etaForQty — keep the two in step. Live slots, earliest first, until `qty` is covered. */
export function slotsForQty(
  slots: DeliverySlot[] | null | undefined,
  qty: number,
  nowMs: number
): { used: DeliverySlot[]; coveredQty: number; want: number } {
  const want = Math.max(1, Math.floor(qty || 1));
  const live = (slots ?? [])
    .filter(
      (s) =>
        s.qty > 0 &&
        !Number.isNaN(Date.parse(s.at)) &&
        (!s.orderBy || Date.parse(s.orderBy) > nowMs)
    )
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const used: DeliverySlot[] = [];
  let covered = 0;
  for (const s of live) {
    if (covered >= want) break;
    covered += s.qty;
    used.push(s);
  }
  return { used, coveredQty: Math.min(covered, want), want };
}

/** "Today" / "Tomorrow" / weekday, plus the Paris hour when there is one. */
function dayLabel(instantMs: number, withHour: boolean, nowMs: number): string {
  const today = parisNow(nowMs);
  const that = parisNow(instantMs);
  const days = Math.round(
    (Date.UTC(that.y, that.m - 1, that.d) - Date.UTC(today.y, today.m - 1, today.d)) / 86_400_000
  );
  const day =
    days === 0
      ? i18n.t('delivery.today')
      : days === 1
        ? i18n.t('delivery.tomorrow')
        : days > 1 && days < 7
          ? weekdays()[new Date(Date.UTC(that.y, that.m - 1, that.d)).getUTCDay()]
          : new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {
              day: 'numeric',
              month: 'short',
              timeZone: PARIS_TZ,
            }).format(new Date(instantMs));
  if (!withHour) return day;
  const hh = String(Math.floor(that.minute / 60)).padStart(2, '0');
  const mm = String(that.minute % 60).padStart(2, '0');
  return `${day} ${hh}:${mm}`;
}

/**
 * The supplier's date for `qty` units, as an Eta. No live slot — the call failed, the supplier
 * gave no date, or every cut-off has passed — is "Date to be confirmed", never a made-up delay;
 * `at` is then +Infinity so it sorts last and compares equal to itself. When the supplier dates
 * fewer units than asked, the label says so: a short quantity must not borrow the others' date.
 */
export function supplierEta(
  slots: DeliverySlot[] | null | undefined,
  qty: number,
  nowMs: number
): Eta {
  const { used, coveredQty, want } = slotsForQty(slots, qty, nowMs);
  const last = used[used.length - 1];
  if (!last) {
    return {
      label: i18n.t('delivery.toBeConfirmed'),
      detail: i18n.t('delivery.noSupplierDate'),
      immediate: false,
      at: Number.POSITIVE_INFINITY,
    };
  }
  const at = Date.parse(last.at);
  const when = dayLabel(at, last.hourKnown, nowMs);
  const label =
    coveredQty < want ? i18n.t('delivery.partial', { covered: coveredQty, qty: want, when }) : when;
  const orderBy = used
    .map((s) => s.orderBy)
    .filter((o): o is string => !!o)
    .sort((a, b) => Date.parse(a) - Date.parse(b))[0];
  const warehouses = [...new Set(used.map((s) => s.warehouse).filter(Boolean))].join(', ');
  const detail = [
    new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...(last.hourKnown ? { hour: '2-digit', minute: '2-digit' } : {}),
      timeZone: PARIS_TZ,
    }).format(new Date(at)),
    i18n.t('delivery.supplierDate'),
    warehouses ? i18n.t('delivery.supplierWarehouse', { warehouse: warehouses }) : '',
    orderBy
      ? i18n.t('delivery.orderBy', { cutoff: dayLabel(Date.parse(orderBy), true, nowMs) })
      : '',
    coveredQty < want ? i18n.t('delivery.restToConfirm', { count: want - coveredQty }) : '',
  ]
    .filter(Boolean)
    .join('\n');
  return { label, detail, immediate: false, at };
}

/**
 * OUR promise rule for one supplier, as the backend relays it (deliverySlots.ts PromiseRule,
 * feedback 2.1 of 2026-10-27): the supplier's cut-off and lead as the owner set them, and the
 * optional hour its parts are on the counter that day. Carries no supplier identity.
 */
export interface PromiseRule {
  /** Today's cut-off ("HH:MM" Paris) — the weekly row's when there is a table, else the legacy one. */
  cutoff: string;
  leadDays: number;
  beforeTime: string | null;
  /**
   * 2026-10-29 — the owner's weekly table (migration 166): one row per ORDER day, Monday first
   * (index 0): the cut-off that day (null = none, the whole day counts as "before") and the lead
   * before / after it, counted in the SUPPLIER's delivery days. Absent = the legacy rule above.
   */
  days?: PromiseDayRule[];
  /** The weekdays the supplier delivers on, ISO 1 (Monday) … 7 (Sunday). */
  deliveryDays?: number[];
  /** The supplier's non-delivery dates, "YYYY-MM-DD". */
  closedDates?: string[];
}
export interface PromiseDayRule {
  cutoff: string | null;
  leadBefore: number;
  leadAfter: number;
}

/** `Date.getUTCDay()` (Sunday-first) → ISO weekday (Monday-first, 1…7). Mirrors the backend's isoWeekday. */
export const isoWeekday = (utcDay: number): number => ((utcDay + 6) % 7) + 1;
/** Mirrors DeliveryPromiseService.RULE_WALK_CAP_DAYS. */
export const RULE_WALK_CAP_DAYS = 60;

/** The ISO weekday of the Paris civil day of `nowMs`. */
function parisIsoDow(nowMs: number): number {
  const { y, m, d } = parisNow(nowMs);
  return isoWeekday(new Date(Date.UTC(y, m - 1, d)).getUTCDay());
}

/** Today's cut-off for a supplier: the weekly row's (null = no cut-off today), else the legacy one. */
export function todayCutoff(rule: PromiseRule, nowMs: number): string | null {
  if (rule.days?.length === 7) return rule.days[parisIsoDow(nowMs) - 1].cutoff;
  return rule.cutoff || null;
}

/**
 * Mirrors DeliveryPromiseService.promisedDayByTable — keep the two in step:
 *   1. the ORDER day's row; before its cut-off (or all day when it has none) → leadBefore, else
 *      leadAfter;
 *   2. walk the lead in the SUPPLIER's delivery days strictly after today: its weekdays minus its
 *      own closed dates (ours do not slow the van);
 *   3. a landing day on which WE are closed rolls forward to the next day the supplier delivers
 *      on which we are open.
 * Null when nothing can be delivered within RULE_WALK_CAP_DAYS → the caller falls back.
 */
export function promisedDayByTable(
  rule: Pick<PromiseRule, 'days' | 'deliveryDays' | 'closedDates'>,
  cal: DeliveryCalendar,
  nowMs: number
): Date | null {
  if (!rule.days || rule.days.length !== 7 || !rule.deliveryDays?.length) return null;
  const { y, m, d, minute } = parisNow(nowMs);
  const row = rule.days[parisIsoDow(nowMs) - 1];
  const before = row.cutoff == null || minute < toMinutes(row.cutoff);
  const lead = Math.max(1, before ? row.leadBefore : row.leadAfter);
  const supplierDays = new Set(rule.deliveryDays);
  const supplierClosed = new Set(rule.closedDates ?? []);
  const ours = new Set(cal.holidays);
  const cursor = new Date(Date.UTC(y, m - 1, d));
  const supplierDelivers = (dt: Date): boolean =>
    supplierDays.has(isoWeekday(dt.getUTCDay())) && !supplierClosed.has(isoDay(dt));
  let walked = 0;
  for (let step = 0; step < RULE_WALK_CAP_DAYS && walked < lead; step++) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    if (supplierDelivers(cursor)) walked++;
  }
  if (walked < lead) return null;
  // Our closure on the landing day: the next day the supplier delivers on which we are open.
  for (let step = 0; step < RULE_WALK_CAP_DAYS && ours.has(isoDay(cursor)); ) {
    do {
      cursor.setUTCDate(cursor.getUTCDate() + 1);
      step++;
    } while (step < RULE_WALK_CAP_DAYS && !supplierDelivers(cursor));
  }
  return cursor;
}

/** `date` (a UTC-midnight civil day) at `hhmm` Paris time → the instant. Mirrors the backend's zonedTime. */
function parisInstant(date: Date, hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  const guess = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    hh || 0,
    mm || 0
  );
  const seen = parisNow(guess).minute;
  return guess + ((hh || 0) * 60 + (mm || 0) - seen) * 60_000;
}

/**
 * THE DATE WE PROMISE for a supplier part (feedback 2.1) — ours, not the supplier's:
 *
 *   1. before the supplier's cut-off the order joins today's collection, after it tomorrow's;
 *   2. delivery is the supplier's lead in working days after that (1 = next day, 2 = the day
 *      after), weekends and closures excluded — the same walk as `promisedDay`;
 *   3. the line reads "Tuesday before 12:00" when the supplier has a "before" hour, "Tuesday"
 *      when it has none.
 *
 * Mirrors DeliveryPromiseService.forExternalLine, which freezes the same answer on the order
 * line. The supplier's own slot date (when it gave one) is kept as a tooltip line, so a date the
 * supplier announces LATER than our promise is visible rather than silently overpromised.
 *
 * Without a rule (a quote cached before the rule existed, an unknown supplier) the global
 * calendar and the supplier's quoted lead stand in — a date, never "to be confirmed".
 */
export function promiseEta(
  rule: PromiseRule | null | undefined,
  cal: DeliveryCalendar,
  nowMs: number,
  opts: { slots?: DeliverySlot[] | null; qty?: number; leadDays?: number | null } = {}
): Eta {
  /* 2026-10-29 — the owner's weekly table when the supplier has one, the single rule otherwise
     (a Redis-cached offer from before the table existed carries none). Same fallbacks as
     DeliveryPromiseService.forExternalLine. */
  const cutoffToday = rule ? todayCutoff(rule, nowMs) : cal.cutoff;
  const legacyCutoff = rule?.cutoff || cal.cutoff;
  const tableDate = rule ? promisedDayByTable(rule, cal, nowMs) : null;
  const { minute } = parisNow(nowMs);
  const row = rule?.days?.length === 7 ? rule.days[parisIsoDow(nowMs) - 1] : null;
  const lead =
    tableDate && row
      ? Math.max(
          1,
          row.cutoff == null || minute < toMinutes(row.cutoff) ? row.leadBefore : row.leadAfter
        )
      : Math.max(1, rule?.leadDays ?? opts.leadDays ?? 1);
  let date =
    tableDate ??
    promisedDay(
      lead,
      { cutoff: legacyCutoff, deliveryTime: cal.deliveryTime, holidays: cal.holidays },
      nowMs
    );
  let at = parisInstant(date, rule?.beforeTime ?? '12:00');
  let label = rule?.beforeTime
    ? `${dayLabel(at, false, nowMs)} ${i18n.t('delivery.before', { time: rule.beforeTime })}`
    : dayLabel(at, false, nowMs);

  /* "Always show the later date between ours and the supplier's" (owner, 2026-10-29): when the
     supplier's own slots date every unit on a LATER Paris day, that day is shown — at the
     supplier's hour when it gave one. Compared by civil day: a day-only slot normalised to 13:00
     must not beat our "before 12:00" on the same day. Mirrors forExternalLine. */
  const cover = slotsForQty(opts.slots, opts.qty ?? 1, nowMs);
  const last = cover.used[cover.used.length - 1];
  let supplierWins = false;
  if (last && cover.coveredQty >= cover.want) {
    const sAt = Date.parse(last.at);
    const sp = parisNow(sAt);
    const sDay = new Date(Date.UTC(sp.y, sp.m - 1, sp.d));
    if (sDay.getTime() > date.getTime()) {
      supplierWins = true;
      date = sDay;
      at = last.hourKnown ? sAt : parisInstant(sDay, cal.deliveryTime);
      label = dayLabel(at, last.hourKnown, nowMs);
    }
  }
  const effectiveCutoff = tableDate ? cutoffToday : legacyCutoff;
  const missed = effectiveCutoff != null && minute >= toMinutes(effectiveCutoff);
  const supplier =
    !supplierWins && opts.slots?.length ? supplierEta(opts.slots, opts.qty ?? 1, nowMs) : null;
  const detail = [
    new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date) +
      (!supplierWins && rule?.beforeTime
        ? ` · ${i18n.t('delivery.before', { time: rule.beforeTime })}`
        : ''),
    effectiveCutoff == null
      ? i18n.t('delivery.noCutoffToday', { count: lead })
      : i18n.t(tableDate ? 'delivery.ourRuleTable' : 'delivery.ourRule', {
          cutoff: effectiveCutoff,
          count: lead,
        }),
    effectiveCutoff == null
      ? ''
      : missed
        ? i18n.t('delivery.cutoffPassed', { cutoff: effectiveCutoff })
        : i18n.t('delivery.orderBy', { cutoff: effectiveCutoff }),
    supplierWins ? i18n.t('delivery.supplierLater') : '',
    supplier && Number.isFinite(supplier.at)
      ? i18n.t('delivery.supplierSays', { when: supplier.label })
      : '',
    tableDate ? i18n.t('delivery.excludedSupplier') : i18n.t('delivery.excluded'),
  ]
    .filter(Boolean)
    .join('\n');
  return { label, detail, immediate: false, at };
}

/**
 * How long the seller has left to hold today's promise.
 *
 * The reference puts this in the header, and it earns its place: it is the only thing on the
 * screen that turns "delivered tomorrow" into a reason to decide NOW. Null once the cut-off has
 * gone, because a countdown to a deadline already missed is worse than none.
 */
export function cutoffCountdown(
  cutoff: string,
  nowMs: number
): { text: string; urgent: boolean } | null {
  const { minute } = parisNow(nowMs);
  const left = toMinutes(cutoff) - minute;
  if (left <= 0) return null;
  const h = Math.floor(left / 60);
  const m = left % 60;
  return { text: h > 0 ? `${h} h ${m} min` : `${m} min`, urgent: left <= 60 };
}

/**
 * A minute-resolution clock for the whole result list.
 *
 * The delivery dates above hinge on a cut-off the counter sits through: at 15:29 every row says
 * "tomorrow" and at 15:31 they all mean "the day after". Recomputing only on re-render would leave
 * an idle screen quoting yesterday's answer through exactly the minute it stops being true.
 */
export function useMinuteTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}
